/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { ChangeEvent } from 'react';
import clsx from 'clsx';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import { Option, Select } from 'carbon-react/lib/components/select';
import Box from 'carbon-react/lib/components/box';
import { Accordion } from 'carbon-react/lib/components/accordion';

import { TABLE_FONT_WEIGHTS, TableCellFillType, TableStyle, RowTypes } from '../../types';
import { useReportBuilderContext } from '../../report-builder.context';
import { ColorPicker } from '../../../forecast/detail/components/color-picker';
import { getClosestNumberInRange } from '../../utils-generic';
import { defaultColor, defaultFillColor, varianceBG } from '../../constants';
import { useI18nConstants } from '../../use-i18n-constants';

import styles from './theme-customization.scss';

const strokeWidthOptions = [0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const fontSizeOptions = [8, 9, 10, 11, 12, 15, 18, 21, 24, 28, 32, 36, 40, 48, 60];

interface Props {
  rowName: string;
  type: RowTypes;
  currentStyle: TableStyle;
  hideEvenFillOptions?: boolean;
  updateStyle: (t: RowTypes, style: Partial<TableStyle>) => void;
}
export const TableRowThemeCustomization = ({
  rowName,
  type,
  currentStyle,
  hideEvenFillOptions,
  updateStyle
}: Props) => {
  const intl = useIntl();
  const { reportColors } = useReportBuilderContext();
  const { borderPositionOptions, fontWeightOptions } = useI18nConstants();

  const handleFontWeightChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const fontWeight = +event.target.value as TABLE_FONT_WEIGHTS;
    if (!fontWeight || fontWeight === currentStyle.fontWeight) {
      return;
    }
    updateStyle(type, { fontWeight });
  };

  const handleFontSizeChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const fontSize = +event.target.value;
    if (!fontSize || fontSize === currentStyle.fontSize) {
      return;
    }
    updateStyle(type, { fontSize });
  };

  const handleColorChange = (color: string) => {
    if (!color || color === currentStyle.color) {
      return;
    }
    updateStyle(type, { color });
  };

  const handleFillChange = (color: string, fillType: TableCellFillType) => {
    if (
      !color ||
      color === currentStyle[fillType] ||
      (color === defaultFillColor && !currentStyle[fillType]) ||
      (color === varianceBG && !currentStyle[fillType] && fillType.includes('fillVariance'))
    ) {
      return;
    }
    updateStyle(type, { [fillType]: color });
  };

  const handleStrokeColorChange = (strokeColor: string) => {
    if (!strokeColor || strokeColor === currentStyle.strokeColor) {
      return;
    }
    updateStyle(type, { strokeColor });
  };

  const handleStrokeBorderWeightChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const strokeWidth = +event.target.value;
    if (!strokeWidth || strokeWidth === currentStyle.strokeWidth) {
      return;
    }
    updateStyle(type, { strokeWidth });
  };

  const handleStrokePositionChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const strokePosition = event.target.value;
    if (!strokePosition || strokePosition === currentStyle.strokePosition) {
      return;
    }
    updateStyle(type, { strokePosition });
  };

  return (
    <div className={clsx(styles.tableRowContent, styles.tableRowContentCarbon)}>
      <Accordion
        borders="none"
        size="small"
        headerSpacing={{
          p: 0
        }}
        disableContentPadding
        title={rowName}
        my={1}
      >
        <Box display="inline-flex" justifyContent="space-between" alignItems="center" width={1} mb={1}>
          <Box flex={4} mr={2}>
            <Select
              id={`${type}-row-font`}
              autoComplete="off"
              className={clsx(styles.select)}
              onChange={handleFontWeightChange}
              value={`${currentStyle.fontWeight}`}
              label={intl.formatMessage({ id: 'common.font', defaultMessage: 'Font' })}
              labelInline
              size="small"
            >
              {fontWeightOptions.map(option => (
                <Option key={option.value} value={`${option.value}`} text={`${option.name}`} />
              ))}
            </Select>
          </Box>
          <Box flex={2} mr={2}>
            <Select
              id={`${type}-row-size`}
              autoComplete="off"
              className={clsx(styles.select)}
              onChange={handleFontSizeChange}
              value={`${
                fontSizeOptions.includes(currentStyle.fontSize || 10)
                  ? currentStyle.fontSize || 10
                  : getClosestNumberInRange(fontSizeOptions, currentStyle.fontSize || 10)
              }`}
              size="small"
            >
              {fontSizeOptions.map(option => (
                <Option key={option} value={`${option}`} text={`${option}`} />
              ))}
            </Select>
          </Box>
          <Box flex={1}>
            <div className={clsx(styles.colorPicker)}>
              <ColorPicker
                id={`${type}-row-font-color`}
                color={currentStyle.color || defaultColor}
                onChange={handleColorChange}
                presetColors={reportColors}
              />
            </div>
          </Box>
        </Box>
        <div className={clsx(styles.fillContainer)}>
          <div className={clsx(styles.fillInput)}>
            <label htmlFor={`${type}-row-fill-color`}>
              <FormattedMessage id="common.fill" defaultMessage="Fill" />
            </label>
            <div className={clsx(styles.colorPicker)}>
              <ColorPicker
                id={`${type}-row-fill-color`}
                color={currentStyle.fill || defaultFillColor}
                onChange={e => handleFillChange(e, 'fill')}
                presetColors={reportColors}
              />
            </div>
          </div>
          <div className={clsx(styles.fillInput)}>
            <label htmlFor={`${type}-row-fill-past-color`}>
              <FormattedMessage id="reporting.historicals" defaultMessage="Historicals" />
            </label>
            <div className={clsx(styles.colorPicker)}>
              <ColorPicker
                id={`${type}-row-fill-past-color`}
                color={currentStyle.fillPast || defaultFillColor}
                onChange={e => handleFillChange(e, 'fillPast')}
                presetColors={reportColors}
              />
            </div>
          </div>
          <div className={clsx(styles.fillInput)}>
            <label htmlFor={`${type}-row-fill-future-color`}>
              <FormattedMessage id="reporting.current_future" defaultMessage="Current/future" />
            </label>
            <div className={clsx(styles.colorPicker)}>
              <ColorPicker
                id={`${type}-row-fill-future-color`}
                color={currentStyle.fillFuture || defaultFillColor}
                onChange={e => handleFillChange(e, 'fillFuture')}
                presetColors={reportColors}
              />
            </div>
          </div>
          <div className={clsx(styles.fillInput)}>
            <label htmlFor={`${type}-row-fill-variance-color`}>
              <FormattedMessage id="reporting.variance" defaultMessage="Variance" />
            </label>
            <div className={clsx(styles.colorPicker)}>
              <ColorPicker
                id={`${type}-row-fill-variance-color`}
                color={currentStyle.fillVariance || varianceBG}
                onChange={e => handleFillChange(e, 'fillVariance')}
                presetColors={reportColors}
              />
            </div>
          </div>
        </div>
        {!hideEvenFillOptions && (
          <div className={clsx(styles.fillContainer)}>
            <div className={clsx(styles.fillInput)}>
              <label htmlFor={`${type}-row-fill-color-even`}>
                <FormattedMessage id="reporting.even_rows" defaultMessage="Even rows" />
              </label>
              <div className={clsx(styles.colorPicker)}>
                <ColorPicker
                  id={`${type}-row-fill-color-even`}
                  color={currentStyle.fillEven || currentStyle.fill || defaultFillColor}
                  onChange={e => handleFillChange(e, 'fillEven')}
                  presetColors={reportColors}
                />
              </div>
            </div>
            <div className={clsx(styles.fillInput)}>
              <label htmlFor={`${type}-row-fill-color-even-past`}>&nbsp;</label>
              <div className={clsx(styles.colorPicker)}>
                <ColorPicker
                  id={`${type}-row-fill-color-even-past`}
                  color={currentStyle.fillPastEven || currentStyle.fillPast || defaultFillColor}
                  onChange={e => handleFillChange(e, 'fillPastEven')}
                  presetColors={reportColors}
                />
              </div>
            </div>
            <div className={clsx(styles.fillInput)}>
              <label htmlFor={`${type}-row-fill-color-even-future`}>&nbsp;</label>
              <div className={clsx(styles.colorPicker)}>
                <ColorPicker
                  id={`${type}-row-fill-color-even-future`}
                  color={currentStyle.fillFutureEven || currentStyle.fillFuture || defaultFillColor}
                  onChange={e => handleFillChange(e, 'fillFutureEven')}
                  presetColors={reportColors}
                />
              </div>
            </div>
            <div className={clsx(styles.fillInput)}>
              <label htmlFor={`${type}-row-fill-variance-color-even`}>&nbsp;</label>
              <div className={clsx(styles.colorPicker)}>
                <ColorPicker
                  id={`${type}-row-fill-variance-color-even`}
                  color={currentStyle.fillVarianceEven || currentStyle.fillVariance || varianceBG}
                  onChange={e => handleFillChange(e, 'fillVarianceEven')}
                  presetColors={reportColors}
                />
              </div>
            </div>
          </div>
        )}
        <Box display="inline-flex" justifyContent="space-between" alignItems="center" width={1} mt={1}>
          <Box flex={1} mr={2} className={styles.withElevatedLabel}>
            <label>
              <FormattedMessage id="predict.stroke" defaultMessage="Stroke" />
            </label>
            <div className={clsx(styles.colorPicker)}>
              <ColorPicker
                id={`${type}-row-font-color`}
                color={currentStyle.color || defaultColor}
                onChange={handleStrokeColorChange}
                presetColors={reportColors}
              />
            </div>
          </Box>
          <Box flex={2} mr={2}>
            <Select
              autoComplete="off"
              className={clsx(styles.select)}
              onChange={handleStrokeBorderWeightChange}
              value={`${
                strokeWidthOptions.includes(currentStyle.strokeWidth || 1)
                  ? currentStyle.strokeWidth || 1
                  : getClosestNumberInRange(strokeWidthOptions, currentStyle.strokeWidth || 1)
              }`}
              size="small"
            >
              {strokeWidthOptions.map(option => (
                <Option key={option} value={`${option}`} text={`${option}`} />
              ))}
            </Select>
          </Box>
          <Box flex={4}>
            <Select
              autoComplete="off"
              className={clsx(styles.select)}
              onChange={handleStrokePositionChange}
              value={currentStyle.strokePosition ?? ''}
              size="small"
            >
              {borderPositionOptions.map(option => (
                <Option key={option.value} value={`${option.value}`} text={`${option.name}`} />
              ))}
            </Select>
          </Box>
        </Box>
      </Accordion>
    </div>
  );
};
